import type { Entries } from './catalog';

/**
 * The copy of second factors, re-authentication and the devices a person is signed in on (E8, ACC-07,
 * ACC-17).
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const factors: Record<'en' | 'es', Entries> = {
	en: {
		'factor.invalid': 'That did not prove it is you. Try again.',
		'factor.ticket': 'That sign-in took too long. Start again.',

		'factors.title': 'Second factor',
		'factors.none': 'Add a second factor so a password alone cannot open your account.',
		'factors.totp': 'Authenticator app',
		'factors.passkey': 'Passkey',
		'factors.added': 'Added {date}',
		'factors.add.totp': 'Add an authenticator app',
		'factors.add.passkey': 'Add a passkey',
		'factors.passkey.unsupported': 'This browser cannot create passkeys.',
		'factors.secret': 'Add this key to your authenticator app, or open it there:',
		'factors.open': 'Open in authenticator app',
		'factors.code': 'The 6-digit code it shows',
		'factors.confirm': 'Turn on',
		'factors.label': 'Name',
		'factors.remove': 'Remove',
		'factors.remove.title': 'Remove {name}?',
		'factors.remove.body': 'Signing in will no longer ask for it.',
		'factors.remove.last': 'It is your only second factor: your recovery codes go with it, and a password alone opens your account again.',
		'factors.recovery': { one: '1 recovery code left', other: '{count} recovery codes left' },
		'factors.recovery.new': 'Make new recovery codes',
		'factors.recovery.renew.title': 'Make new recovery codes?',
		'factors.recovery.renew.body': 'Your current recovery codes stop working at once. The new ones are shown only once.',
		'factors.recovery.renew': 'Make new codes',
		'factors.recovery.shown': 'Keep these recovery codes somewhere safe. Each one signs you in once when your second factor is not at hand. They are shown only now.',
		'factors.recovery.download': 'Download',
		'factors.done': 'Your second factor is on.',

		'secondstep.title': 'Confirm it is you',
		'secondstep.lead.totp': 'Enter the code from your authenticator app.',
		'secondstep.lead.passkey': 'Use your passkey.',
		'secondstep.lead.recovery': 'Enter one of your recovery codes.',
		'secondstep.code': 'Code',
		'secondstep.passkey': 'Use passkey',
		'secondstep.submit': 'Continue',
		'secondstep.other.totp': 'Use your authenticator app instead',
		'secondstep.other.passkey': 'Use a passkey instead',
		'secondstep.other.recovery': 'Use a recovery code instead',
		'secondstep.attempts': { one: '1 attempt left.', other: '{count} attempts left.' },
		'secondstep.restart': 'Sign in again',

		'reauth.title': 'Confirm it is you',
		'reauth.password': 'Enter your password to continue.',
		'reauth.password.label': 'Password',
		'reauth.password.wrong': 'That password is not right.',
		'reauth.totp': 'Enter the code from your authenticator app to continue.',
		'reauth.totp.label': 'Code',
		'reauth.totp.wrong': 'That code is not right.',
		'reauth.accept': 'Continue',

		'devices.title': 'Where you are signed in',
		'devices.unknown': 'Unknown browser',
		'devices.on': '{browser} on {system}',
		'devices.place.unknown': 'Unknown place',
		'devices.current': 'This browser',
		'devices.seen': 'Last used {date}',
		'devices.signed': 'Signed in {date}',
		'devices.opened': 'Opened {products}',
		'devices.signout': 'Sign out',
		'devices.others': 'Sign out of other sessions',
		'devices.others.title': 'Sign out of every other session?',
		'devices.others.body': 'Every other browser and product signed in to this account is signed out. This browser stays signed in.',
		'devices.ended': { one: 'One session was signed out.', other: '{count} sessions were signed out.' }
	},
	es: {
		'factor.invalid': 'Eso no confirmó que eres tú. Vuelve a intentarlo.',
		'factor.ticket': 'Ese inicio de sesión tardó demasiado. Empieza de nuevo.',

		'factors.title': 'Segundo factor',
		'factors.none': 'Añade un segundo factor para que una contraseña sola no abra tu cuenta.',
		'factors.totp': 'App de autenticación',
		'factors.passkey': 'Llave de acceso',
		'factors.added': 'Añadido el {date}',
		'factors.add.totp': 'Añadir una app de autenticación',
		'factors.add.passkey': 'Añadir una llave de acceso',
		'factors.passkey.unsupported': 'Este navegador no puede crear llaves de acceso.',
		'factors.secret': 'Añade esta clave a tu app de autenticación, o ábrela allí:',
		'factors.open': 'Abrir en la app de autenticación',
		'factors.code': 'El código de 6 dígitos que muestra',
		'factors.confirm': 'Activar',
		'factors.label': 'Nombre',
		'factors.remove': 'Quitar',
		'factors.remove.title': '¿Quitar {name}?',
		'factors.remove.body': 'Iniciar sesión ya no lo pedirá.',
		'factors.remove.last': 'Es tu único segundo factor: tus códigos de recuperación se van con él y una contraseña sola vuelve a abrir tu cuenta.',
		'factors.recovery': { one: 'Queda 1 código de recuperación', other: 'Quedan {count} códigos de recuperación' },
		'factors.recovery.new': 'Crear códigos de recuperación nuevos',
		'factors.recovery.renew.title': '¿Crear códigos de recuperación nuevos?',
		'factors.recovery.renew.body': 'Tus códigos de recuperación actuales dejan de funcionar al instante. Los nuevos se muestran solo una vez.',
		'factors.recovery.renew': 'Crear códigos nuevos',
		'factors.recovery.shown': 'Guarda estos códigos de recuperación en un lugar seguro. Cada uno te deja entrar una vez cuando no tienes tu segundo factor. Solo se muestran ahora.',
		'factors.recovery.download': 'Descargar',
		'factors.done': 'Tu segundo factor está activado.',

		'secondstep.title': 'Confirma que eres tú',
		'secondstep.lead.totp': 'Escribe el código de tu app de autenticación.',
		'secondstep.lead.passkey': 'Usa tu llave de acceso.',
		'secondstep.lead.recovery': 'Escribe uno de tus códigos de recuperación.',
		'secondstep.code': 'Código',
		'secondstep.passkey': 'Usar la llave de acceso',
		'secondstep.submit': 'Continuar',
		'secondstep.other.totp': 'Usar la app de autenticación',
		'secondstep.other.passkey': 'Usar una llave de acceso',
		'secondstep.other.recovery': 'Usar un código de recuperación',
		'secondstep.attempts': { one: 'Queda 1 intento.', other: 'Quedan {count} intentos.' },
		'secondstep.restart': 'Iniciar sesión de nuevo',

		'reauth.title': 'Confirma que eres tú',
		'reauth.password': 'Escribe tu contraseña para continuar.',
		'reauth.password.label': 'Contraseña',
		'reauth.password.wrong': 'Esa contraseña no es correcta.',
		'reauth.totp': 'Escribe el código de tu app de autenticación para continuar.',
		'reauth.totp.label': 'Código',
		'reauth.totp.wrong': 'Ese código no es correcto.',
		'reauth.accept': 'Continuar',

		'devices.title': 'Dónde tienes la sesión iniciada',
		'devices.unknown': 'Navegador desconocido',
		'devices.on': '{browser} en {system}',
		'devices.place.unknown': 'Lugar desconocido',
		'devices.current': 'Este navegador',
		'devices.seen': 'Usado por última vez el {date}',
		'devices.signed': 'Sesión iniciada el {date}',
		'devices.opened': 'Abrió {products}',
		'devices.signout': 'Cerrar sesión',
		'devices.others': 'Cerrar las otras sesiones',
		'devices.others.title': '¿Cerrar todas las otras sesiones?',
		'devices.others.body': 'Se cierra la sesión de todos los demás navegadores y productos con esta cuenta. Este navegador sigue con la sesión iniciada.',
		'devices.ended': { one: 'Se cerró una sesión.', other: 'Se cerraron {count} sesiones.' }
	}
};
